import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { Lesson } from './interfaces'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useAdminRoutes } from '@shared/lib/routes'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import {
  LESSON_EMPTY_CELL,
  formatLessonStart,
  getLessonStatusNote,
  getLessonTasksDueRange,
  getLessonTasksText,
  getLessonTutorName,
  getLessonTypeBadgeColor,
} from './lessons'
import { useLessonPrivileges } from './lesson-privileges.hook'
import { useLessonsStore } from './store'

interface LessonRow {
  lesson: Lesson
  start: string
  typeLabel: string
  typeColor: AppBadgeColor
  statusNote: string
  tutorName: string | null
  tutorUrl: string | null
  tasksText: string
  tasksUrl: string | null
  editUrl: string
}

interface TableLessonsReturn {
  rows: ComputedRef<LessonRow[]>
  isLoading: ComputedRef<boolean>
  hasLessons: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  refCanManage: ComputedRef<boolean>
  refIsAllSelected: ComputedRef<boolean>
  isSelected: (id: number) => boolean
  toggleSelected: (id: number) => void
  toggleAllSelected: () => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableLessons = (): TableLessonsReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useLessonsStore()
  const listsStore = useListsStore()
  const { canManageBookings } = useLessonPrivileges()

  const disciplineId = route.params.id as string

  const isLoading = computed<boolean>(() => store.isLoading)
  const hasLessons = computed<boolean>(() => store.hasLessons)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  /** Row checkboxes and the row menu are for lesson managers, as in the legacy tab. */
  const refCanManage = computed<boolean>(() => canManageBookings.value)

  const getTasksUrl = (lesson: Lesson): string | null => {
    const range = getLessonTasksDueRange(lesson.tasks)
    const path = resolveUrl(AppRoute.DisciplinesTasks, { id: disciplineId })
    if (!range || !path) return null

    return `${path}?${new URLSearchParams(range).toString()}`
  }

  const rows = computed<LessonRow[]>(() => {
    const lists = listsStore.getLists

    return store.getLessons.map(lesson => ({
      lesson,
      start: formatLessonStart(lesson),
      typeLabel: lists?.booking.type[lesson.type] ?? lesson.type,
      typeColor: getLessonTypeBadgeColor(lesson.type),
      statusNote: getLessonStatusNote(lesson, {
        reasonCategories: lists?.booking.reason_category ?? {},
        reasons: lists?.booking.change_status_reasons.ALL ?? {},
      }),
      tutorName: lesson.tutor ? getLessonTutorName(lesson.tutor, lists?.tutor.paid_label ?? {}) : null,
      tutorUrl: lesson.tutor ? useAdminRoutes('admin.tutor.show', { tutor_id: lesson.tutor.id }) : null,
      tasksText: lists ? getLessonTasksText(lesson.tasks, lists.tutor_task.type) : LESSON_EMPTY_CELL,
      tasksUrl: getTasksUrl(lesson),
      editUrl: resolveUrl(AppRoute.LessonsEdit, { id: disciplineId, lessonId: lesson.id }) ?? '',
    }))
  })

  const refIsAllSelected = computed<boolean>(() =>
    store.hasLessons && store.getLessons.every(lesson => store.getSelectedIds.includes(lesson.id)))

  const isSelected = (id: number): boolean => store.getSelectedIds.includes(id)

  const toggleSelected = (id: number): void => {
    store.toggleSelected(id)
  }

  const toggleAllSelected = (): void => {
    store.toggleAllSelected()
  }

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    rows,
    isLoading,
    hasLessons,
    meta,
    perPageOptions,
    refCanManage,
    refIsAllSelected,
    isSelected,
    toggleSelected,
    toggleAllSelected,
    setChangePage,
    setChangePerPage,
  }
}
